import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import { noop } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import {
  ErrorPortal,
  WidgetContainerRefProp,
} from '../../../editor/features/startBotPromoters/common/StartBotsDialogueEditor/WidgetByType/WidgetByType';
import TagsCRUDActions from '@mm/state/actions/TagsCRUDActions';
import WidgetModel from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { tagsOfKinds } from '@mm/state/selectors/tags';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import { useCommonWidgetHandlers } from '../../helpers/widget-helpers/use-common-widget-handlers';
import { ActivePathSelection } from '../common/ActivePathSelection/ActivePathSelection';
import { BubbleTextInput } from '../common/BubbleTextInput/BubbleTextInput';
import { FacebookPageIcon } from '../common/FacebookPageIcon/FacebookPageIcon';
import { getNodeName, PathGraphContext } from '../common/PathGraph/PathGraph';
import { WidgetControls } from '../common/WidetControls/WidgetControls';
import { OtnTagForm } from './OtnTagForm/OtnTagForm';
import { Alert } from '@mm/components';
import './OtnWidget.pcss';
import { WidgetUnsupportedError } from '../errors/WidgetUnsupportedError/WidgetUnsupportedError';
import { useResolutionInfo } from '@mm/hooks';
import { PlatformType } from '@mm/state/model/CampaignsModel';

export const BUTTON_NAME = 'Notify Me';

type Props = {
  widget: WidgetModel;
  isExpanded?: boolean;
  draggableProvided?: DraggableProvided;
  isSmsRelated?: boolean;
  forInstagram?: boolean;
  smsOnly?: boolean;
  showAttributePicker?: boolean;
  platforms?: PlatformType[];
  usedInSlideout?: boolean;
} & WidgetContainerRefProp;

export const OtnWidget = (props: Props) => {
  const {
    widget,
    draggableProvided,
    isExpanded = true,
    isSmsRelated,
    smsOnly,
    showAttributePicker = true,
    forInstagram,
    platforms,
    usedInSlideout,
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();

  const { setActiveNode, pathGraph, removeNodeAndSuccessors } =
    useContext(PathGraphContext);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const page = useSelector(activeFacebookPageSelector);
  const tags = useSelector((state: StoreState) => tagsOfKinds(state, ['otn']));
  const [editMode, setEditMode] = useState(true);
  const fullEditMode = isExpanded && editMode;

  const commonWidgetHandlers = useCommonWidgetHandlers();

  const isActive = !!pathGraph?.node(getNodeName(widget.id, 'widget'));
  const selectedTag = widget.config.tag?.name;

  const openButton = () => {
    setActiveNode?.({
      id: widget.id,
      type: 'widget',
      dialogueId: widget.config.pageId,
      action: 'open',
    });
  };

  const onRemove = () =>
    commonWidgetHandlers.updateField(widget, 'config.tag.name', null);
  const onSelect = (name: string) =>
    commonWidgetHandlers.updateField(widget, 'config.tag.name', name);

  useEffect(() => {
    if (page?.id) {
      dispatch(TagsCRUDActions.getAll(page.id, false, ['otn', 'regular']));
    }
  }, [page?.id]);

  const beforeDestroy = useCallback(() => {
    if (isActive) {
      setActiveNode?.({
        id: widget.id,
        type: 'widget',
        action: 'close',
      });
    }
    removeNodeAndSuccessors?.({
      id: widget.id,
      type: 'widget',
      actionType: 'delete',
      parentId: widget.pageId,
      parentType: 'dialogue',
    });
  }, [widget.id, isActive]);

  const { isDesktop } = useResolutionInfo();
  const [hasAttributesInBody, setHasAttributesInBody] = useState(false);
  useEffect(() => {
    setHasAttributesInBody(/{{.+}}/.test(widget.config.title || ''));
  }, [widget.config.title]);

  return (
    <div className="otn-widget">
      <div className="otn-widget__title">
        <span className="otn-widget__title-bold">1-Time Notification</span>{' '}
        (Facebook only)
      </div>
      <div className="otn-widget__main-area-wrapper">
        <FacebookPageIcon />

        <div className="otn-widget__right-area">
          <div className="otn-widget__right-top-area">
            {!fullEditMode && (
              <div
                className="otn-widget__show-mode"
                onClick={() => setEditMode(true)}
              >
                <div className="otn-widget__title-show">
                  {widget.config.title}
                </div>
                <div className="otn-widget__facebook-widget-notification">
                  <span>
                    By clicking "Notify Me" you may receive a message from
                  </span>
                  <span>
                    {page?.name || 'the page'} in the future about this topic.
                  </span>
                </div>
              </div>
            )}
            {fullEditMode && (
              <OnClickElsewhere
                className="otn-widget__text-area-wrapper"
                handleClick={() => setEditMode(false)}
              >
                <div className="otn-widget__editor">
                  <BubbleTextInput
                    className="otn-widget__bubble-text-input"
                    maxLength={65}
                    updateText={newText =>
                      commonWidgetHandlers.updateField(
                        widget,
                        'config.title',
                        newText,
                      )
                    }
                    value={widget.config.title}
                    editMode={true}
                    onClick={noop}
                    emojiPickerColor="#9a9aa2"
                    attributePickerColor="#9a9aa2"
                    showAttributePicker={showAttributePicker}
                  />
                  <div className="otn-widget__facebook-widget-notification">
                    <span>
                      By clicking "Notify Me" you may receive a message from
                    </span>
                    <span>
                      {page?.name || 'the page'} in the future about this topic.
                    </span>
                  </div>
                  <hr className="otn-widget__content-separator" />
                  <OtnTagForm
                    tags={tags}
                    selectedTag={selectedTag}
                    onSelect={onSelect}
                    onRemove={onRemove}
                  />
                </div>
              </OnClickElsewhere>
            )}

            {hasAttributesInBody && !showAttributePicker && (
              <ErrorPortal widget={widget}>
                <div className="otn-widget__alert-container">
                  <Alert
                    className="otn-widget__warning"
                    type={'error'}
                    showCloseIcon={false}
                  >
                    Attributes are not available on the first widget since we
                    only get contact’s profile AFTER their first response.
                  </Alert>
                </div>
              </ErrorPortal>
            )}

            {isSmsRelated && !widget.supportedByPlatforms.sms && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError
                  className="otn-widget__warning"
                  isError={smsOnly}
                  type="sms"
                />
              </ErrorPortal>
            )}

            {forInstagram && !widget.supportedByPlatforms.instagram && (
              <ErrorPortal widget={widget}>
                <WidgetUnsupportedError
                  className="otn-widget__warning"
                  isError
                  type="instagram"
                />
              </ErrorPortal>
            )}

            <WidgetControls
              widget={widget}
              draggableProvided={draggableProvided}
              beforeDestroy={beforeDestroy}
              platforms={platforms}
            />
          </div>

          <div className="otn-widget__buttons-area">
            <div onClick={openButton} className="otn-button__button">
              <span className="otn-button__button-text">{BUTTON_NAME}</span>
              <div className="otn-button__icon-container">
                <PathIcon className="otn-button__path-icon" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {isActive && (
        <ActivePathSelection
          chatbotId={chatbotId}
          widget={widget}
          isActive={isActive}
          name={BUTTON_NAME}
          activePathType="regular"
          platforms={platforms}
          usedInSlideout={usedInSlideout}
        />
      )}
    </div>
  );
};
